import { useCallback, useId, useMemo, useRef, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import SelectSkeleton from 'personalization-lib/components/shared/display/SelectSkeleton';
import ManageVariantsLink from 'personalization-lib/components/shared/links/ManageVariantsLink';
import VariantSelectValue from 'personalization-lib/components/variantSelect/VariantSelectValue';
import VariantSelectOption from 'personalization-lib/components/variantSelect/VariantSelectOption';
import VariantSelectFooter from 'personalization-lib/components/variantSelect/VariantSelectFooter';
import VariantSelectErrorState from 'personalization-lib/components/variantSelect/VariantSelectErrorState';
import UpgradePointButton from 'personalization-lib/components/shared/upgrades/UpgradePointButton';
import VariantsInProgressNotificationBadge from 'personalization-lib/components/variantSelect/VariantsInProgressNotificationBadge';
import withInlineErrorHandling from 'personalization-lib/components/shared/hocs/withInlineErrorHandling';
import withPersonalizationAccess from 'personalization-lib/components/shared/hocs/withPersonalizationAccess';
import { useContentEditorPersonalizationContext } from 'personalization-lib/contexts/ContentEditorPersonalizationContext';
import useVariantSelectOptions from 'personalization-lib/hooks/variants/useVariantSelectOptions';
import useButtonClickTracker from 'personalization-lib/hooks/tracking/library/useButtonClickTracker';
import useAiExecutionStatus from 'personalization-lib/hooks/variants/useAiExecutionStatus';
import { hasAssetPersonalizationScope } from 'personalization-lib/utils/auth/assetAccess';
import { getIsInIframe } from 'personalization-lib/utils/location';
import { ABM_MINIMUM_SEARCH_COUNT, VARIANT_SELECT_MODES } from 'personalization-lib/constants/variantSelect';
import { jsx as _jsx } from "react/jsx-runtime";
const ContentEditorVariantSelect = ({
  value,
  upgradeKey,
  onChange,
  onError,
  'data-onboarding-target': onboardingTarget,
  actionSource,
  selectProps,
  unpersonalizedFallback,
  mode = VARIANT_SELECT_MODES.DEFAULT
}) => {
  const hasHadRequestsInProgressRef = useRef(false);
  const id = useId();
  const _instancePrefix = useMemo(() => `variant-select-${id}`, [id]); // unique prefix for aria attributes, allows us to associate the in progress notification badge with the select
  const [open, setOpen] = useState(false);
  const {
    assetData,
    loading: assetDataLoading
  } = useContentEditorPersonalizationContext();
  const strategyId = assetData === null || assetData === void 0 ? void 0 : assetData.strategyId;
  const assetType = assetData === null || assetData === void 0 ? void 0 : assetData.assetType;
  const {
    options,
    selectedOption,
    loading,
    error,
    valueNotFound
  } = useVariantSelectOptions({
    value,
    strategyId,
    onError,
    mode
  });
  const {
    requestsInProgress
  } = useAiExecutionStatus(strategyId);
  const isInIFrame = getIsInIframe();
  const isAbmMode = mode === VARIANT_SELECT_MODES.ABM;
  const trackButtonClick = useButtonClickTracker({
    actionSource
  });
  const handleOpenChange = useCallback(e => {
    setOpen(e.target.value);
  }, []);
  const handleCloseSelect = useCallback(() => setOpen(false), []);

  // Render the current selection from the resolved option so the base/all-visitors
  // asset still shows in the trigger even when it is hidden from the ABM dropdown.
  const renderValue = useCallback(props => /*#__PURE__*/_jsx(VariantSelectValue, Object.assign({}, props, {
    option: selectedOption !== null && selectedOption !== void 0 ? selectedOption : props.option
  })), [selectedOption]);
  const handleChange = useCallback(e => {
    trackButtonClick.variantSelect();
    onChange(e.target.value);
  }, [onChange, trackButtonClick]);
  if (requestsInProgress > 0) {
    // eslint-disable-next-line react-hooks/refs
    hasHadRequestsInProgressRef.current = true; // If there has been variants in progress, we want to announce when they are all done
  }
  if (isInIFrame) {
    return strategyId && /*#__PURE__*/_jsx(ManageVariantsLink, {
      strategyId: strategyId
    });
  }
  if (loading || assetDataLoading) {
    return /*#__PURE__*/_jsx(SelectSkeleton, Object.assign({
      "data-test-id": "variant-select-loading"
    }, selectProps));
  }
  if (!strategyId) {
    return unpersonalizedFallback;
  }
  if (assetType && !hasAssetPersonalizationScope(assetType)) {
    return upgradeKey && /*#__PURE__*/_jsx(UpgradePointButton, {
      upgradeKey: upgradeKey,
      "data-test-id": "variant-select-upgrade",
      use: "tertiary-light",
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "personalizationLib.variantSelect.placeholder"
      })
    });
  }
  if (valueNotFound) {
    return /*#__PURE__*/_jsx(VariantSelectErrorState, {});
  }
  const variantActionsFooter = /*#__PURE__*/_jsx(VariantSelectFooter, {
    strategyId: strategyId,
    onCreateClick: handleCloseSelect
  });
  // ABM variants are managed outside of the editor, so the dropdown has no management actions
  const dropdownFooter = isAbmMode ? undefined : variantActionsFooter;
  return /*#__PURE__*/_jsx(VariantsInProgressNotificationBadge, {
    count: requestsInProgress
    // eslint-disable-next-line react-hooks/refs
    ,
    hasHadRequestsInProgress: hasHadRequestsInProgressRef.current,
    _instancePrefix: _instancePrefix,
    children: /*#__PURE__*/_jsx(Select, Object.assign({
      "data-test-id": "variant-select",
      "aria-describedby": `${_instancePrefix}--notification`,
      "data-onboarding-target": onboardingTarget !== null && onboardingTarget !== void 0 ? onboardingTarget : 'variant-select',
      open: open,
      buttonUse: "tertiary-light",
      buttonSize: "small",
      menuWidth: 400,
      maxInlineSize: "280px",
      value: value,
      valueComponent: renderValue,
      error: Boolean(error),
      disabled: Boolean(error),
      dropdownFooter: dropdownFooter,
      minimumSearchCount: isAbmMode ? ABM_MINIMUM_SEARCH_COUNT : undefined,
      placeholder: I18n.text('personalizationLib.variantSelect.placeholder'),
      onOpenChange: handleOpenChange,
      onChange: handleChange,
      optionComponent: VariantSelectOption,
      options: options
    }, selectProps))
  });
};
export default withInlineErrorHandling(withPersonalizationAccess(ContentEditorVariantSelect), {
  errorFallback: /*#__PURE__*/_jsx(VariantSelectErrorState, {})
});